import { useEffectiveFeatures } from '@/hooks/useEffectiveFeatures'
import { useLayouts } from '@/hooks/useLayout'
import { useMidiStore } from '@/stores/midiStore'

interface PlanUsageProps {
  /** Opens the upgrade dialog — wired by the parent page. */
  onUpgrade?: () => void
}

/**
 * Free-tier-only usage strip. Shows layouts / MIDI routes used vs
 * allowed, plus an upgrade CTA. Self-hides for Pro and Studio —
 * those tiers should feel uncluttered, not reminded of limits.
 */
export function PlanUsage({ onUpgrade }: PlanUsageProps) {
  const { features, tier } = useEffectiveFeatures()
  const { data: layoutsData } = useLayouts()
  const routes = useMidiStore(s => s.routes)

  if (!features || tier !== 'free') return null

  const layoutCount = Array.isArray(layoutsData) ? layoutsData.length : 0
  const maxLayouts = features.maxLayouts
  const routeCount = routes.length
  const maxRoutes = features.maxMidiRoutes ?? Number.POSITIVE_INFINITY

  return (
    <div className="sm:col-span-2 flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-xl bg-accent-muted border border-[color:var(--accent-halo)] px-4 py-3">
      <div className="text-xs text-content-primary">
        <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-accent mr-2">
          Free plan
        </span>
        <span className="font-mono text-accent">
          {layoutCount} / {maxLayouts}
        </span>{' '}
        layouts ·{' '}
        <span className="font-mono text-accent">
          {routeCount} / {Number.isFinite(maxRoutes) ? maxRoutes : '∞'}
        </span>{' '}
        MIDI routes ·{' '}
        <span className="text-content-secondary">
          Nate08 kit only · 0 MIDI pads
        </span>
      </div>
      {onUpgrade && (
        <button
          type="button"
          onClick={onUpgrade}
          className="btn-accent-gradient btn-sm font-semibold self-start sm:self-auto whitespace-nowrap"
        >
          Upgrade — from $12/mo →
        </button>
      )}
    </div>
  )
}
